import React from "react";
import { Form, Select as AntSelect, Button as AntButton, Col } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Select from "react-select";
import { toast } from "react-toastify";

const { Option } = AntSelect;

const CustomerDetailsSection = ({
  formData,
  setFormData,
  customerList,
  isLoading,
  errors,
  setErrors,
  calculateOrderSummary,
  setOrderSummary,
  customerTypeOptions,
  planTypeOptions,
  handleCustomerTypeChange,
  handleCustomerPaymentTypeChange,
  setOpenAddCustomer,
  isCustomerPreSelected
}) => {
  const selectStyles = (errorFieldName) => ({
    control: (provided) => ({
      ...provided,
      borderColor: errors[errorFieldName] ? "#dc3545" : provided.borderColor,
      boxShadow: errors[errorFieldName]
        ? "0 0 0 0.2rem rgba(220, 53, 69, 0.25)"
        : provided.boxShadow,
      minHeight: 22,
      height: 33,
      overflow: "hidden",
    }),
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    singleValue: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
  });


  const filteredCustomerList = customerList.filter(
    (c) => c.subscription_status !== 1 || c.id === formData.user_id,
  );

  return (
    <>
      {/* Customer Name */}
      <Col xs={24} sm={24} md={16}>
        <Form.Item
          label="Customer Name"
          validateStatus={errors.user_id ? "error" : ""}
          help={errors.user_id}
        >
          <AntSelect
            showSearch
            placeholder="Select Customer"
             disabled={isCustomerPreSelected}
            optionFilterProp="children"
            filterOption={(input, option) => {
              // Access the customer object from the option props
              const customer = customerList.find((c) => c.id === option.value);
              if (!customer) return false;

              const searchText = input.toLowerCase();
              const name = (customer.name || "").toLowerCase();
              const email = (customer.email || "").toLowerCase();

              // Search in both name and email
              return name.includes(searchText) || email.includes(searchText);
            }}
            value={formData.user_id || undefined}
            onChange={(value) => {
              const selectedCustomer = customerList.find((c) => c.id === value);

              // Reset all dependent fields when customer changes
              setFormData((prev) => ({
                ...prev,
                user_id: value,
                customer_type: null, // Reset customer type (Paid/Free Trial)
                is_attorney: null, // Reset plan type (Attorney/Corporate)
                plan_id: null,
                plan_type: null,
                plan_price: 0,
                total_amount: 0,
                final_amount: 0,
                total_tm_count: 0,
                end_date: "",
              }));

              setErrors((prev) => ({
                ...prev,
                user_id: "",
                customer_type: "",
                is_attorney: "",
              }));

              // Update order summary
              if (selectedCustomer) {
                setOrderSummary((prev) => ({
                  ...prev,
                  customerName: selectedCustomer.name || "-",
                  customerEmail: selectedCustomer.email || "-",
                  customerMobile: selectedCustomer.mobile || "-",
                  customerType: "-",
                  planName: "-",
                  planType: "-",
                  planAmount: 0,
                  totalTMCount: 0,
                  finalAmount: 0,
                  gstAmount: 0,
                  totalAmount: 0,
                  attorneyDetails: [],
                  end_date: "-",
                }));
              } else {
                // Reset entire order summary if no customer
                setOrderSummary({
                  customerName: "-",
                  customerEmail: "-",
                  customerMobile: "-",
                  customerType: "-",
                  planName: "-",
                  planType: "-",
                  planAmount: 0,
                  totalTMCount: 0,
                  finalAmount: 0,
                  gstAmount: 0,
                  totalAmount: 0,
                  // Matches the form's default; clearing the customer is not a
                  // decision about tax.
                  includeGST: true,
                  attorneyDetails: [],
                  end_date: "-",
                });
              }
            }}
            size="large"
            loading={isLoading}
          >
            {filteredCustomerList.map((customer) => (
              <Option key={customer.id} value={customer.id}>
                {customer.name} - {customer.email}
              </Option>
            ))}
          </AntSelect>
        </Form.Item>
      </Col>

      {/* Add Customer Button */}
      <Col xs={24} sm={24} md={8}>
        <Form.Item label=" " colon={false}>
          <AntButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setOpenAddCustomer(true)}
            size="large"
            className="w-100"
            style={{
              backgroundColor: "#52c41a",
              borderColor: "#52c41a",
            }}
          >
            Add Customer
          </AntButton>
        </Form.Item>
      </Col>

      {/* Customer Type (Paid/Free Trial) */}
      <Col xs={24} sm={12}>
        <Form.Item
          label="Subscription Type"
          validateStatus={errors.customer_type ? "error" : ""}
          help={errors.customer_type}
        >
          <AntSelect
            placeholder="Select Subscription Type"
            value={formData.customer_type || undefined}
            onChange={(value) => {
              if (!formData.user_id) {
                toast.warning("Please select Customer Name first.");
                return;
              }

              setFormData((prev) => ({
                ...prev,
                customer_type: value,
                // end_date: value === "2" ? prev.end_date : "",
                end_date: "", // Reset end_date when customer type changes
              }));

              setOrderSummary((prev) => ({
                ...prev,
                customerType:
                  value === "1" ? "Paid" : value === "2" ? "Free Trial" : "-",
                // end_date: value === "2" ? prev.end_date : "-",
                end_date: "_",
              }));

              setErrors((prev) => ({
                ...prev,
                customer_type: "",
              }));
            }}
            size="large"
            disabled={!formData.user_id}
          >
            {customerTypeOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </AntSelect>
        </Form.Item>
      </Col>

      {/* Plan Type (Attorney/Corporate) */}
      <Col xs={24} sm={12}>
        <Form.Item
          label="Customer Type"
          validateStatus={errors.is_attorney ? "error" : ""}
          help={errors.is_attorney}
        >
          <Select
            placeholder="Select Customer Type"
            options={planTypeOptions}
            value={
              planTypeOptions.find((o) => o.value === formData.is_attorney) ||
              null
            }
            onChange={handleCustomerTypeChange}
            styles={{ ...selectStyles("is_attorney"), menuPortal: (base) => ({ ...base, zIndex: 1060 }) }}
            isDisabled={!formData.user_id}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </Form.Item>
      </Col>
    </>
  );
};

export default CustomerDetailsSection;
